import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { getApiUrl } from 'utils/config';
const CLOSE_FALLBACK_TIMEOUT_MS = 1500;
/**
 * Popup callback page for iFrame SSO flow.
 *
 * This page is loaded ONLY in the popup window after successful SSO authentication.
 * It has a valid Kratos session (cookie = first-party in popup context).
 *
 * Flow:
 * 1. Calls POST /api/v1/auth/create-iframe-token with session cookie
 * 2. Receives JWT token from backend
 * 3. Sends token to iFrame via window.opener.postMessage()
 * 4. Closes itself
 *
 * Edge cases handled:
 * - window.opener is null (COOP header severed connection) → shows error message
 * - window.close() doesn't work → shows "close manually" fallback after 1.5s
 * - Token creation fails → shows error message
 */
const IframeCallback = () => {
    const [error, setError] = useState(null);
    useEffect(() => {
        /*
         * Called in the popup after successful SSO.
         * Has valid Kratos session cookie (first-party context).
         */
        fetch(`${getApiUrl()}/api/v1/auth/create-iframe-token`, {
            // Send session cookie
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
        })
            .then(async (res) => {
            if (!res.ok)
                throw new Error(`HTTP ${res.status}`);
            return res.json();
        })
            .then((data) => {
            if (data.token) {
                if (window.opener) {
                    // Send token to the iFrame (origin-verified)
                    window.opener.postMessage({ token: data.token, type: 'SSO_COMPLETE' }, window.location.origin);
                    window.close();
                    // Fallback: if window.close() doesn't work (e.g., not opened via window.open)
                    setTimeout(() => {
                        setError('Anmeldung erfolgreich. Bitte schließen Sie dieses Fenster manuell.');
                    }, CLOSE_FALLBACK_TIMEOUT_MS);
                }
                else {
                    /*
                     * COOP (Cross-Origin-Opener-Policy) severed window.opener during SSO redirect chain.
                     * This can happen if WebUntis or an intermediate OAuth page sets COOP: same-origin.
                     */
                    setError('Anmeldung erfolgreich, aber das Fenster kann nicht mit dem übergeordneten Element kommunizieren. Bitte schließen Sie dieses Fenster und laden Sie die Stunde in WebUntis neu.');
                }
            }
        })
            .catch((err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to create iframe token:', err);
            setError('Anmeldung fehlgeschlagen. Bitte schließen Sie dieses Fenster und versuchen Sie es erneut.');
        });
    }, []);
    if (error) {
        return (_jsx(Stack, { alignItems: "center", justifyContent: "center", spacing: 2, sx: { mt: 4 }, children: _jsx(Typography, { color: "error", children: error }) }));
    }
    return (_jsxs(Stack, { alignItems: "center", justifyContent: "center", spacing: 2, sx: { mt: 4 }, children: [_jsx(CircularProgress, {}), _jsx(Typography, { children: "Anmeldung wird abgeschlossen..." })] }));
};
export default IframeCallback;
